import React, { useContext, useState } from 'react'
import { Router, RouteComponentProps, navigate } from '@reach/router'
import useFetch from 'use-http'

import { generateCustomTokenOptions, getErrorMessage } from '~/api'
import CenteredContentLayout from '~/layouts/CenteredContentLayout'
import { SessionContext } from '~/contexts/SessionContext'
import { AuthContext } from '~/contexts/AuthContext'
import StatusCheck from './StatusCheck'
import UpgradeResentEmail from '~/components/UpgradeResentEmail'
import UpgradeSuccess from '~/components/UpgradeSuccess'
import SetPhone from '~/components/SetPhone'
import EnterPin from '~/components/EnterPin'
import SetPin from '~/components/SetPin'

type Props = RouteComponentProps

const AutoUpgrade = (_props: Props) => {
  const { token } = useContext(AuthContext)
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null)
  const [sessionDetails, setSessionDetails] = useContext(SessionContext)
  const { request, response } = useFetch({
    ...generateCustomTokenOptions(token, 'upgrade'),
    credentials: 'include',
  })

  const backToLogin = () => navigate('/')

  const onBack = () => history.back()

  const onPhoneClear = () => {
    setErrorMessage(null)
    setSessionDetails({ ...sessionDetails, phone: null })
  }

  const onPhoneNext = async (phone: string) => {
    setErrorMessage(null)
    setSessionDetails({ ...sessionDetails, phone: phone })

    await request.post('/api/v1/upgrades/preupgrade', { identifier: phone })

    if (response.ok) {
      navigate(response.data.data === true ? 'enter-pin' : 'set-pin')
    } else {
      navigate('/error')
    }
  }

  const onPinConfirm = async (pin: string) => {
    setErrorMessage(null)

    const params = {
      phone: sessionDetails.phone,
      pin: pin,
    }

    await request.post('/api/v1/upgrades/link', params)

    if (response.ok) {
      const data = response.data.data

      setSessionDetails({
        ...sessionDetails,
        referrer: data.strategy_type === 'Educator' ? 'playground' : 'home-web',
        firstName: data.first_name,
        strategyType: data.strategy_type,
        strategyID: data.strategy_id,
        identifier: data.upgrade_email_address,
      })

      navigate(data.strategy_type === 'Xplorer' ? '/hubguest' : 'success')
    } else if (response.status != 401) {
      navigate('/error')
    } else {
      setErrorMessage(getErrorMessage(response))
    }
  }

  const onPinCreate = async (pin: string) => {
    setErrorMessage(null)

    const params = {
      phone: sessionDetails.phone,
      pin: pin,
    }

    await request.post('/api/v1/upgrades/auto/finalise', params)

    if (response.ok) {
      const data = response.data.data

      if (data.upgrade_status === 'completed') {
        setSessionDetails({
          ...sessionDetails,
          referrer: data.strategy_type === 'Educator' ? 'playground' : 'home-web',
          firstName: data.first_name,
          strategyType: data.strategy_type,
          strategyID: data.strategy_id,
          identifier: data.upgrade_email_address,
        })

        navigate(data.strategy_type === 'Xplorer' ? '/hubguest' : 'success')
      } else if (data.upgrade_status === 'sent') {
        setSessionDetails({ ...sessionDetails, identifier: data.upgrade_email_address })
        navigate('resent-email')
      } else {
        navigate('/error')
      }
    } else {
      setErrorMessage(getErrorMessage(response))
    }
  }

  return (
    <CenteredContentLayout>
      <Router>
        <StatusCheck path="/" />
        <SetPhone onClear={onPhoneClear} onBack={backToLogin} onNext={onPhoneNext} path="phone" />
        <EnterPin errorMessage={errorMessage} onBack={onBack} onNext={onPinConfirm} path="enter-pin" />
        <SetPin errorMessage={errorMessage} onBack={onBack} onNext={onPinCreate} path="set-pin" />
        <UpgradeSuccess path="success" />
        <UpgradeResentEmail path="resent-email" />
      </Router>
    </CenteredContentLayout>
  )
}

export default AutoUpgrade
